import React, { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  X,
  TrendingUp,
  Download,
  UserPlus,
  UserMinus,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Inbox,
} from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Modal from './UI/Modal';
import PillToggle from './UI/PillToggle';
import DebouncedSearchInput from './UI/DebouncedSearchInput';
import { getTodayInSingaporeTimezone } from '../utils/dateUtils';

// ── Helpers ──────────────────────────────────────────────────────────────
const formatTenure = (days) => {
  if (days === null || days === undefined || isNaN(days)) return null;
  if (days < 31) return `${days}d`;
  const years = Math.floor(days / 365);
  const months = Math.round((days % 365) / 30);
  if (years === 0 && months === 0) return '<1m';
  if (years === 0) return `${months}m`;
  if (months === 0) return `${years}y`;
  return `${years}y ${months}m`;
};

const safeFormatDate = (iso, pattern) => {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), pattern);
  } catch {
    return iso;
  }
};

const SummaryTile = ({ icon: Icon, value, label, sub, tone }) => {
  const palette = {
    green: { shell: 'bg-green-50 border-green-200', icon: 'text-green-600', value: 'text-green-900', label: 'text-green-800', sub: 'text-green-600' },
    red: { shell: 'bg-red-50 border-red-200', icon: 'text-red-600', value: 'text-red-900', label: 'text-red-800', sub: 'text-red-600' },
    emerald: { shell: 'bg-emerald-50 border-emerald-200', icon: 'text-emerald-600', value: 'text-emerald-900', label: 'text-emerald-800', sub: 'text-emerald-600' },
    gray: { shell: 'bg-gray-50 border-gray-200', icon: 'text-gray-500', value: 'text-gray-900', label: 'text-gray-700', sub: 'text-gray-500' },
  };
  const c = palette[tone] || palette.gray;
  return (
    <div className={`rounded-lg border px-2 py-2.5 sm:p-4 ${c.shell}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${c.icon}`} aria-hidden="true" />
        <p className={`text-[10px] sm:text-xs font-medium ${c.label}`}>{label}</p>
      </div>
      <p className={`text-xl sm:text-3xl font-bold leading-none tabular-nums ${c.value}`}>{value}</p>
      {sub && <p className={`text-[10px] sm:text-xs mt-1 ${c.sub}`}>{sub}</p>}
    </div>
  );
};

const ClassificationBadge = ({ label }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap">
    {label}
  </span>
);

const ReasonBadge = ({ label }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
    {label}
  </span>
);

const TimelineRow = ({ event }) => {
  const joined = event.type === 'joined';
  return (
    <li className="relative pl-9 sm:pl-10 pb-4 last:pb-0">
      {/* Rail dot */}
      <span
        className={`absolute left-1.5 sm:left-2 top-1 z-10 flex items-center justify-center w-6 h-6 rounded-full ring-4 ring-white ${
          joined ? 'bg-green-100' : 'bg-red-100'
        }`}
      >
        {joined ? (
          <UserPlus className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />
        ) : (
          <UserMinus className="w-3.5 h-3.5 text-red-600" aria-hidden="true" />
        )}
      </span>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900 truncate">{event.name}</span>
            <span className="text-xs text-gray-400 tabular-nums">{event.staff_id}</span>
            <ClassificationBadge label={event.classification_label} />
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {[event.department, event.designation].filter(Boolean).join(' · ') || 'No department'}
          </p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span
              className={`text-[11px] font-medium ${joined ? 'text-green-700' : 'text-red-700'}`}
            >
              {joined ? 'Joined' : 'Left'}
            </span>
            {!joined && event.end_reason_label && <ReasonBadge label={event.end_reason_label} />}
            {event.tenure_days != null && (
              <span className="text-[11px] text-gray-400">
                {joined ? `${formatTenure(event.tenure_days)} so far` : `${formatTenure(event.tenure_days)} tenure`}
              </span>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <p className="text-xs font-medium text-gray-900 whitespace-nowrap">{safeFormatDate(event.date, 'd MMM')}</p>
          <p className="text-[10px] text-gray-400 whitespace-nowrap">{safeFormatDate(event.date, 'yyyy')}</p>
        </div>
      </div>
    </li>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="bg-white/95 border border-gray-200 rounded-lg px-3 py-2 shadow-md text-xs">
      <p className="font-semibold text-gray-900 mb-1">{label}</p>
      <p className="text-green-700">Joined: <span className="font-medium tabular-nums">{row.joined}</span></p>
      <p className="text-red-700">Left: <span className="font-medium tabular-nums">{row.left}</span></p>
      <p className="text-gray-700">Net: <span className="font-medium tabular-nums">{row.net >= 0 ? '+' : ''}{row.net}</span></p>
    </div>
  );
};

const HeadcountChangeModal = ({ isOpen, onClose, data, loading, error }) => {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const summary = data?.summary;
  const events = data?.events || [];
  const monthly = data?.monthly || [];

  const filteredEvents = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) => {
      if (filter !== 'all' && e.type !== filter) return false;
      if (!q) return true;
      return (
        (e.name || '').toLowerCase().includes(q) ||
        (e.staff_id || '').toLowerCase().includes(q) ||
        (e.department || '').toLowerCase().includes(q) ||
        (e.designation || '').toLowerCase().includes(q)
      );
    });
  }, [events, filter, query]);

  // Group filtered events by month label for the timeline rail.
  const groupedEvents = useMemo(() => {
    const groups = [];
    const indexByKey = new Map();
    filteredEvents.forEach((e) => {
      const key = e.date ? e.date.slice(0, 7) : 'unknown';
      const label = e.date ? safeFormatDate(e.date, 'MMMM yyyy') : 'Unknown date';
      if (!indexByKey.has(key)) {
        indexByKey.set(key, groups.length);
        groups.push({ key, label, items: [] });
      }
      groups[indexByKey.get(key)].items.push(e);
    });
    return groups;
  }, [filteredEvents]);

  const handleExport = () => {
    const header = ['Type', 'Name', 'Staff ID', 'Classification', 'Department', 'Designation', 'Date', 'Reason', 'Tenure (days)'];
    const rows = events.map((e) => [
      e.type === 'joined' ? 'Joined' : 'Left',
      `"${(e.name || '').replace(/"/g, '""')}"`,
      e.staff_id || '',
      e.classification_label || '',
      `"${(e.department || '').replace(/"/g, '""')}"`,
      `"${(e.designation || '').replace(/"/g, '""')}"`,
      e.date || '',
      e.end_reason_label || '',
      e.tenure_days ?? '',
    ]);
    const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `headcount-movements-${getTodayInSingaporeTimezone()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const netTone = summary && summary.net > 0 ? 'emerald' : summary && summary.net < 0 ? 'red' : 'gray';
  const NetIcon = summary && summary.net > 0 ? ArrowUpRight : summary && summary.net < 0 ? ArrowDownRight : Minus;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      theme="admin"
      customHeader={
        <div className="bg-white border-b border-gray-200 px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between rounded-t-2xl gap-2">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-2xl font-bold text-gray-900 truncate">Headcount Change</h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {data?.period ? `Who joined & left · ${data.period}` : 'Who joined & left · trailing 12 months'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={!events.length}
              className="icon-button-admin focus-visible:ring-2 focus-visible:ring-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Export to CSV"
              title="Export to CSV"
            >
              <Download className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="modal-close-admin" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      }
      contentClassName="px-4 py-4 sm:px-8 sm:py-6"
    >
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="loading-spinner-admin mr-3" />
          <span className="text-sm text-gray-600">Loading headcount timeline...</span>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Inbox className="w-10 h-10 text-gray-300 mb-3" />
          <p className="text-sm font-medium text-gray-700">Couldn't load headcount movements</p>
          <p className="text-xs text-gray-500 mt-1">Please try again in a moment.</p>
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {/* Summary strip */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <SummaryTile
              icon={UserPlus}
              value={summary?.joined ?? 0}
              label="Joined"
              sub={`≈ ${summary?.avg_monthly_joined ?? 0}/mo`}
              tone="green"
            />
            <SummaryTile
              icon={UserMinus}
              value={summary?.left ?? 0}
              label="Left"
              sub={`≈ ${summary?.avg_monthly_left ?? 0}/mo`}
              tone="red"
            />
            <SummaryTile
              icon={NetIcon}
              value={`${summary && summary.net > 0 ? '+' : ''}${summary?.net ?? 0}`}
              label="Net change"
              sub={`≈ ${summary && summary.avg_monthly_net > 0 ? '+' : ''}${summary?.avg_monthly_net ?? 0}/mo`}
              tone={netTone}
            />
          </div>

          {/* Reconciliation note */}
          <p className="text-[11px] sm:text-xs text-gray-500 -mt-1">
            The dashboard card shows the monthly average ({summary && summary.avg_monthly_net > 0 ? '+' : ''}
            {summary?.avg_monthly_net ?? 0}/mo) of these {summary?.joined ?? 0} joins and {summary?.left ?? 0} separations
            over the trailing 12 months.
          </p>

          {/* Monthly movement chart */}
          {monthly.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">
                Monthly Movement
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={monthly} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#9ca3af" tickLine={false} />
                  <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                  <Bar dataKey="joined" name="Joined" fill="#16a34a" radius={[3, 3, 0, 0]} maxBarSize={22} />
                  <Bar dataKey="left" name="Left" fill="#dc2626" radius={[3, 3, 0, 0]} maxBarSize={22} />
                  <Line type="monotone" dataKey="net" name="Net" stroke="#059669" strokeWidth={2} dot={{ r: 2.5, fill: '#059669' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                Timeline
                <span className="ml-2 text-xs font-normal text-gray-400">
                  {filteredEvents.length} {filteredEvents.length === 1 ? 'movement' : 'movements'}
                </span>
              </h3>
              <div className="flex items-center gap-2">
                <DebouncedSearchInput
                  value={query}
                  onSearch={setQuery}
                  placeholder="Search name, dept..."
                  theme="admin"
                  className="sm:w-56"
                />
                <PillToggle
                  value={filter}
                  onChange={setFilter}
                  ariaLabel="Filter movements"
                  variant="compact"
                  theme="admin"
                  options={[
                    { id: 'all', label: 'All' },
                    { id: 'joined', label: 'Joined' },
                    { id: 'left', label: 'Left' },
                  ]}
                />
              </div>
            </div>

            {groupedEvents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Inbox className="w-10 h-10 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-700">No movements match your filters</p>
                <p className="text-xs text-gray-500 mt-1">Try clearing the search or switching to "All".</p>
              </div>
            ) : (
              <div className="space-y-5">
                {groupedEvents.map((group) => (
                  <div key={group.key}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                      {group.label}
                    </p>
                    <ol className="relative border-l border-gray-200 ml-3">
                      {group.items.map((event, idx) => (
                        <TimelineRow key={`${event.staff_id}-${event.type}-${idx}`} event={event} />
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default HeadcountChangeModal;
